import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Link } from 'react-router-dom';
import { AppIcon } from '../components/AppIcon';
import type { IconName } from '../assets/icons';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Package,
  Users,
  ArrowRight,
  Building2,
  Clock,
  CheckCircle,
  AlertTriangle,
  PieChart,
  LineChart,
  Activity,
  Target,
  Zap,
  Eye,
  FileText,
  Calendar,
  Bell,
  Search,
  Smartphone,
  Settings,
  Award,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download,
  Share2,
  Layers,
  Grid,
  Brain,
  Sparkles
} from 'lucide-react';

const CounterAnimation = ({ value, duration = 2000, prefix = '', suffix = '' }: { value: number; duration?: number; prefix?: string; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

const DashboardMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
              <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
              <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
            </div>
            <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
              Analytics Dashboard
            </h3>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
            <Search className="w-4 h-4" />
            <Bell className="w-4 h-4" />
            <div className="w-7 h-7 rounded-full bg-gray-300"></div>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <Badge className="bg-green-100 text-green-800">+12%</Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CounterAnimation value={247} />
            </div>
            <p className="text-sm text-gray-600">Active Orders</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <Badge className="bg-blue-100 text-blue-800">-8%</Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              $<CounterAnimation value={18450} />
            </div>
            <p className="text-sm text-gray-600">Monthly Spend</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-5 h-5" style={{ color: '#f58b01' }} />
              <Badge style={{ backgroundColor: '#fef3e2', color: '#f58b01' }}>98%</Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CounterAnimation value={1234} />
            </div>
            <p className="text-sm text-gray-600">Signs in Stock</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-purple-600" />
              <Badge className="bg-purple-100 text-purple-800">94%</Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CounterAnimation value={96} />%
            </div>
            <p className="text-sm text-gray-600">On-Time Rate</p>
          </Card>
        </div>

        {/* Chart Visualization */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Installation Trends</h4>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-white">Last 30 Days</Badge>
              <button className="p-1 hover:bg-white rounded">
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Simple Bar Chart Illustration */}
          <div className="flex items-end justify-between h-40 gap-2">
            {[65, 78, 52, 88, 94, 71, 85, 79, 92, 88, 95, 89].map((height, index) => (
              <motion.div
                key={index}
                initial={{ height: 0 }}
                animate={{ height: `${height}%` }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                className="flex-1 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg relative group cursor-pointer"
                style={{ minWidth: '20px' }}
              >
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  {height} orders
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-between mt-2 text-xs text-gray-600">
            <span>Jan</span>
            <span>Jun</span>
            <span>Dec</span>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Recent Activity</h4>
          {[
            { icon: CheckCircle, text: 'Installation completed at 456 Oak Ave', time: '2m ago', color: 'text-green-600' },
            { icon: AlertTriangle, text: 'Delayed removal at 789 Pine St', time: '15m ago', color: 'text-yellow-600' },
            { icon: Package, text: '24 signs restocked to inventory', time: '1h ago', color: 'text-blue-600' }
          ].map((activity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.5 + index * 0.1 }}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <activity.icon className={`w-4 h-4 ${activity.color}`} />
                <p className="text-sm text-gray-700">{activity.text}</p>
              </div>
              <span className="text-xs text-gray-500">{activity.time}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const FeatureSection = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

export default function ReportingFeaturePage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const coreFeatures: { icon: IconName; title: string; points: string[] }[] = [
    {
      icon: 'reportsOperationsDashboard',
      title: 'Operations Dashboard',
      points: [
        'View all active installs & removals in one place',
        'Filter by status, office, vendor, or date',
        'Instantly spot what\'s done, delayed, or upcoming'
      ],
    },
    {
      icon: 'reportsFinancialInsightsTracking',
      title: 'Financial Insights & Spend Tracking',
      points: [
        'Track spend by office, agent, or vendor',
        'See taxes, fees, and 5% vendor discounts',
        'Understand true costs and ROI across jobs'
      ],
    },
    {
      icon: 'reportsSignageInventoryReport',
      title: 'Signage Inventory Reports',
      points: [
        'Know how many signs are deployed or in stock',
        'Identify damaged or missing items quickly',
        'Monitor restock trends and usage by office'
      ],
    },
    {
      icon: 'reportsVendorPerformance',
      title: 'Vendor Performance Reports',
      points: [
        'Compare turnaround time & accuracy between vendors',
        'See who delivers fastest and most reliably',
        'Identify delays, errors, and cost inefficiencies'
      ],
    },
    {
      icon: 'reportsSmartForecastingAlerts',
      title: 'Smart Forecasting & Alerts',
      points: [
        'AI alerts for unusual spend or delays',
        'Predict installs and sign needs by quarter',
        'Spot issues before they impact operations'
      ],
    },
    {
      icon: 'reportsMobileAccessKeyMetrics',
      title: 'Mobile Access to Key Metrics',
      points: [
        'Track all reports from any device',
        'Get instant notifications for key updates',
        'Keep managers & agents aligned on performance'
      ],
    }
  ];

  const benefits: { icon: IconName; title: string; description: string }[] = [
    {
      icon: 'reportsRealTimeClarity',
      title: 'Real-time Clarity',
      description: 'Complete visibility on installs, removals, and spend',
    },
    {
      icon: 'reportsSimplifiedDecisionMaking',
      title: 'Simplified Decision-Making',
      description: 'Data-driven insights for managers and admins',
    },
    {
      icon: 'reportsTransparentPerformance',
      title: 'Transparent Performance',
      description: 'Clear metrics for vendors and offices',
    },
    {
      icon: 'reportsCostControl',
      title: 'Cost Control',
      description: 'Improved accountability and budget management',
    },
    {
      icon: 'reportsTimeSavings',
      title: 'Time Savings',
      description: 'Saves hours of manual tracking each week',
    }
  ];

  const problems = [
    'No unified visibility across offices and agents',
    'Time lost reconciling invoices and spend',
    'No easy way to monitor vendor performance',
    'Guesswork around sign usage and inventory needs'
  ];

  const solutions = [
    'Automated, real-time dashboards — no spreadsheets',
    'Cost and usage tracking across every listing',
    'Vendor performance metrics for accountability',
    'Forecasting tools to plan inventory and budgets'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 opacity-30"
        >
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: BarChart3, x: '10%', y: '15%', delay: 0 },
            { Icon: PieChart, x: '85%', y: '25%', delay: 1 },
            { Icon: TrendingUp, x: '15%', y: '70%', delay: 2 },
            { Icon: LineChart, x: '80%', y: '65%', delay: 1.5 },
            { Icon: Activity, x: '50%', y: '20%', delay: 0.5 },
            { Icon: DollarSign, x: '25%', y: '45%', delay: 2.5 },
            { Icon: Target, x: '75%', y: '45%', delay: 1.2 },
            { Icon: Brain, x: '60%', y: '80%', delay: 0.8 }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.35, 0],
                scale: [0.8, 1.2, 0.8],
                y: [-20, 20, -20]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut"
              }}
              className="absolute"
              style={{ left: item.x, top: item.y }}
            >
              <item.Icon className="w-8 h-8 md:w-12 md:h-12 text-blue-500" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    RealEsy Reporting — Real Estate Operations Made Transparent
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    RealEsy Reporting gives brokerages and agents complete visibility into installations, removals, orders, inventory, and vendor performance — all in one place.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                    >
                      Get Started
                      <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Why Reporting Matters Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div
                className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-full mb-6"
                style={{ background: "linear-gradient(90deg, #073399 0%, #236DD9 100%)" }}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Why Reporting Matters</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                From Chaos to Clarity
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                Without accurate, connected reports, brokerages and agents waste time tracking data manually — across invoices, spreadsheets, and emails. RealEsy eliminates that chaos with built-in intelligence designed for real estate workflows.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* The Problem */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-8 rounded-2xl border-2"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">The Problem</h3>
                </div>
                <div className="space-y-4">
                  {problems.map((problem, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{problem}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* The Solution */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-8 rounded-2xl border-2"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">The Solution</h3>
                </div>
                <div className="space-y-4">
                  {solutions.map((solution, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{solution}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Core Features Section */}
      <FeatureSection className="bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full mb-6 shadow-lg" style={{ color: '#f58b01' }}>
                <Settings className="w-5 h-5" />
                <span className="font-medium">Core Features</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Powerful Analytics at Your Fingertips
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Everything you need to understand and optimize your real estate operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={36} alt={feature.title} />
                  </div>
                  <h3 className="font-bold text-xl mb-4 text-gray-900">{feature.title}</h3>
                  <ul className="space-y-3">
                    {feature.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                        <span className="leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Benefits Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full mb-6 shadow-lg" style={{ color: '#1D4ED8' }}>
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">Why Brokerages Love It</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Benefits That Drive Results
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Transform how your team tracks, understands, and optimizes operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-gray-50 p-6 rounded-2xl border border-gray-200 text-center group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={benefit.icon} size={36} alt={benefit.title} />
                  </div>
                  <h3 className="font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Integration Section */}
      <FeatureSection className="bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full mb-6 shadow-lg">
                <Layers className="w-6 h-6 text-indigo-600" />
                <span className="text-lg font-medium text-indigo-700">Designed for Collaboration</span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Connected Across Your Operations
              </h2>

              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed max-w-3xl mx-auto">
                RealEsy Reporting connects seamlessly across your operations — from WorkDrive, Vendor Portal, and SmartRead AI to external tools like QuickBooks, Xero, and other ERP systems.
              </p>

              <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                  {(
                    [
                      { icon: 'reportsIntegrationWorkdrive', title: 'WorkDrive', desc: 'Operations' },
                      { icon: 'reportsIntegrationVendorPortal', title: 'Vendor Portal', desc: 'Performance' },
                      { icon: 'reportsIntegrationSmartreadAi', title: 'SmartRead AI', desc: 'Intelligence' },
                      { icon: 'reportsIntegrationQuickbooks', title: 'QuickBooks', desc: 'Accounting' },
                      { icon: 'reportsIntegrationXero', title: 'Xero', desc: 'Financials' },
                      { icon: 'reportsIntegrationErpSystems', title: 'ERP Systems', desc: 'Enterprise' },
                    ] as { icon: IconName; title: string; desc: string }[]
                  ).map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="text-center"
                    >
                      <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                        <AppIcon name={item.icon} size={36} alt={item.title} />
                      </div>
                      <h3 className="font-bold mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 pt-8 border-t border-gray-200"
                >
                  <p className="text-gray-700 text-lg font-medium">
                    Get unified insights that bridge operations, finances, and vendor performance — so every action is measurable and every decision is informed.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </FeatureSection>

      {/* Enhanced Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 opacity-20"
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut"
              }}
              className="absolute w-8 h-8"
            >
              {i % 2 === 0 ?
                <BarChart3 className="w-full h-full text-white" /> :
                <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              }
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              See how reporting transforms your operations.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              Book a demo to explore real-time dashboards for your brokerage or team.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center opacity-100">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white opacity-100 w-full sm:w-auto"
                >
                  Get Started
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
